/**
 * Bulk import of account lead history (owner 2026-10-25, decision 9b), opened
 * from the Modules Catalog "Account lead history" card.
 *
 * Flow: download the template → choose a .xlsx / .csv → parse in the browser
 * (`parseLeadHistorySheet`, lib/account-lead-history.ts) → DRY RUN
 * (`dryRun: true`, nothing written) → per-company preview (before → after,
 * errors per row) → Apply (the same rows, `dryRun: false`) → result summary.
 *
 * ⚠️ A company's rows REPLACE ITS WHOLE HISTORY (server whole-set replace).
 * Companies with errors are skipped by the server, never written in part; the
 * Apply button counts only the `ok` companies.
 *
 * The applied call re-sends the rows the dry run previewed. If the data changed
 * between the two calls the server's answer wins, and that answer is what the
 * summary shows.
 */
import { useState } from 'react';
import {
  Badge,
  Button,
  Field,
  Input,
  Modal,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  TableWrap,
  type BadgeTone,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { StatTile } from '@/components/ui/StatTile';
import { api, ApiError } from '@/lib/api';
import {
  LEAD_HISTORY_TEMPLATE_CSV,
  accountLeadErrorMessage,
  parseLeadHistorySheet,
  type ParsedLeadHistorySheet,
} from '@/lib/account-lead-history';
import { saveBlob } from '@/lib/download';
import { formatDateOnly, pluralise } from '@/lib/format';
import type {
  AccountLeadImportCompanyResult,
  AccountLeadImportResult,
  AccountLeadImportRowInput,
} from '@/lib/types';

const TEMPLATE_FILENAME = 'account-lead-history-template.csv';

type PeriodSummary = AccountLeadImportCompanyResult['before'][number];

const STATUS_LABEL: Record<AccountLeadImportCompanyResult['status'], string> = {
  ok: 'Ready',
  unchanged: 'Unchanged',
  error: 'Error',
};

const APPLIED_STATUS_LABEL: Record<AccountLeadImportCompanyResult['status'], string> = {
  ok: 'Applied',
  unchanged: 'Unchanged',
  error: 'Skipped',
};

const STATUS_TONE: Record<AccountLeadImportCompanyResult['status'], BadgeTone> = {
  ok: 'success',
  unchanged: 'neutral',
  error: 'danger',
};

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.formMessage ?? error.message;
  return fallback;
}

function companyKey(company: AccountLeadImportCompanyResult, index: number): string {
  return company.company ? `c${company.company.id}` : `r${company.rowNumbers.join('-')}-${index}`;
}

export function AccountLeadImportModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose(): void;
}) {
  const [fileName, setFileName] = useState<string | null>(null);
  const [rows, setRows] = useState<AccountLeadImportRowInput[] | null>(null);
  /* Spreadsheet line of each sent row: import row n (1-based) is sheetRows[n - 1]. */
  const [sheetRows, setSheetRows] = useState<number[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [busy, setBusy] = useState<'parsing' | 'checking' | 'applying' | null>(null);
  const [preview, setPreview] = useState<AccountLeadImportResult | null>(null);
  const [applied, setApplied] = useState<AccountLeadImportResult | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);
  /* Bumped on reset so the native file input is remounted empty. */
  const [inputKey, setInputKey] = useState(0);

  const reset = () => {
    setFileName(null);
    setRows(null);
    setSheetRows([]);
    setParseErrors([]);
    setPreview(null);
    setApplied(null);
    setRequestError(null);
    setInputKey((k) => k + 1);
  };

  const close = () => {
    if (busy === 'applying') return;
    reset();
    onClose();
  };

  const downloadTemplate = () => {
    saveBlob(TEMPLATE_FILENAME, new Blob([LEAD_HISTORY_TEMPLATE_CSV], { type: 'text/csv;charset=utf-8' }));
  };

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setFileName(file.name);
    setRows(null);
    setSheetRows([]);
    setParseErrors([]);
    setPreview(null);
    setApplied(null);
    setRequestError(null);
    setBusy('parsing');
    let parsed: ParsedLeadHistorySheet;
    try {
      parsed = await parseLeadHistorySheet(file);
    } catch {
      setParseErrors(['That file could not be read as a CSV or spreadsheet.']);
      setBusy(null);
      return;
    }
    /*
     * ⚠️ Any parse error stops here: the parser drops bad rows, and a company's
     * rows REPLACE its whole history, so sending the rest could wipe periods.
     */
    setParseErrors(parsed.errors);
    if (parsed.errors.length > 0 || parsed.rows.length === 0) {
      if (parsed.errors.length === 0) setParseErrors(['That file has no rows to import.']);
      setBusy(null);
      return;
    }
    setRows(parsed.rows);
    setSheetRows(parsed.sheetRowNumbers);
    setBusy('checking');
    try {
      const response = await api.importAccountLeadHistory({ rows: parsed.rows, dryRun: true });
      setPreview(response.data);
    } catch (error) {
      setRequestError(errorMessage(error, 'The file could not be checked.'));
    } finally {
      setBusy(null);
    }
  };

  const apply = async () => {
    if (!rows || !preview || busy) return;
    setBusy('applying');
    setRequestError(null);
    try {
      const response = await api.importAccountLeadHistory({ rows, dryRun: false });
      setApplied(response.data);
    } catch (error) {
      setRequestError(errorMessage(error, 'The import failed. Nothing was changed for the company that failed.'));
    } finally {
      setBusy(null);
    }
  };

  const shown = applied ?? preview;
  const okCount = preview?.totals.ok ?? 0;
  const applyDisabledReason =
    preview && okCount === 0 ? 'No company in this file is ready to apply.' : null;

  return (
    <Modal
      open={open}
      onClose={close}
      title="Import account lead history"
      size="xl"
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
          {applyDisabledReason && !applied && (
            <span className="text-xs text-fg-muted" role="status">
              {applyDisabledReason}
            </span>
          )}
          <Button variant="secondary" onClick={close} disabled={busy === 'applying'}>
            {applied ? 'Done' : 'Cancel'}
          </Button>
          {preview && !applied && (
            <span className="inline-flex" title={applyDisabledReason ?? undefined}>
              <Button
                onClick={() => void apply()}
                loading={busy === 'applying'}
                disabled={okCount === 0 || busy !== null}
              >
                {`Apply ${pluralise(okCount, 'company', 'companies')}`}
              </Button>
            </span>
          )}
        </div>
      }
    >
      <div className="grid gap-4">
        {!applied && (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <Button variant="secondary" onClick={downloadTemplate} className="shrink-0">
              Download template
            </Button>
            <div className="min-w-0 flex-1">
              <Field label="File">
                {(props) => (
                  <Input
                    {...props}
                    key={inputKey}
                    type="file"
                    accept=".xlsx,.csv"
                    disabled={busy !== null}
                    onChange={(e) => {
                      void onFile(e.target.files?.[0]);
                    }}
                  />
                )}
              </Field>
            </div>
          </div>
        )}

        {busy === 'parsing' && (
          <p className="m-0 text-sm text-fg-muted" role="status">
            Reading {fileName ?? 'the file'}…
          </p>
        )}
        {busy === 'checking' && (
          <p className="m-0 text-sm text-fg-muted" role="status">
            Checking {rows ? pluralise(rows.length, 'row') : 'rows'}…
          </p>
        )}

        {parseErrors.length > 0 && (
          <Notice tone="danger" role="alert" title="This file cannot be imported">
            <ul className="m-0 grid gap-0.5 pl-4">
              {parseErrors.map((message, i) => (
                <li key={i} className="[overflow-wrap:anywhere]">
                  {message}
                </li>
              ))}
            </ul>
          </Notice>
        )}

        {requestError && (
          <Notice tone="danger" role="alert">
            <span className="[overflow-wrap:anywhere]">{requestError}</span>
          </Notice>
        )}

        {applied && (
          <Notice tone={applied.applied ? 'success' : 'warning'} role="status">
            {applied.applied
              ? `Account lead history replaced for ${pluralise(applied.totals.ok, 'company', 'companies')}.`
              : 'Nothing was changed.'}
            {applied.totals.errors > 0 &&
              ` ${pluralise(applied.totals.errors, 'company', 'companies')} with errors ${
                applied.totals.errors === 1 ? 'was' : 'were'
              } skipped.`}
          </Notice>
        )}

        {shown && <Totals result={shown} applied={applied !== null} />}

        {shown && shown.companies.length > 0 && (
          <CompanyResults
            companies={shown.companies}
            applied={applied !== null}
            toSheetRow={(n) => sheetRows[n - 1] ?? n}
          />
        )}
      </div>
    </Modal>
  );
}

function Totals({ result, applied }: { result: AccountLeadImportResult; applied: boolean }) {
  const { totals } = result;
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
      <StatTile label="Companies" value={totals.companies} />
      <StatTile label={applied ? 'Applied' : 'Ready'} value={totals.ok} />
      <StatTile label="Unchanged" value={totals.unchanged} />
      <StatTile label={applied ? 'Skipped' : 'Errors'} value={totals.errors} />
      <StatTile label="Periods" value={totals.periods} />
    </div>
  );
}

function PeriodList({ periods }: { periods: PeriodSummary[] }) {
  if (periods.length === 0) return <span className="text-fg-muted">—</span>;
  return (
    <ul className="m-0 grid list-none gap-0.5 p-0">
      {periods.map((p, i) => (
        <li key={i} className="[overflow-wrap:anywhere]">
          <span className="text-fg">{p.userName}</span>{' '}
          <span className="text-fg-muted tabular-nums">
            {formatDateOnly(p.startsOn)} – {p.endsOn === null ? 'Current' : formatDateOnly(p.endsOn)}
          </span>
        </li>
      ))}
    </ul>
  );
}

type ToSheetRow = (importRow: number) => number;

function StatusCell({
  company,
  applied,
  toSheetRow,
}: {
  company: AccountLeadImportCompanyResult;
  applied: boolean;
  toSheetRow: ToSheetRow;
}) {
  return (
    <div className="grid gap-1">
      <span>
        <Badge tone={STATUS_TONE[company.status]}>
          {(applied ? APPLIED_STATUS_LABEL : STATUS_LABEL)[company.status]}
        </Badge>
      </span>
      {company.errors.length > 0 && (
        <ul className="m-0 grid list-none gap-0.5 p-0 text-xs text-danger">
          {company.errors.map((err, i) => (
            <li key={`${err.code}-${i}`} className="[overflow-wrap:anywhere]">
              {err.row !== null && <span className="tabular-nums">Row {toSheetRow(err.row)}: </span>}
              {err.message || accountLeadErrorMessage(err.code) || err.code}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function companyLabel(company: AccountLeadImportCompanyResult, toSheetRow: ToSheetRow): string {
  if (company.company) return company.company.name;
  const lines = company.rowNumbers.map(toSheetRow);
  return lines.length === 1 ? `Row ${lines[0]}` : `Rows ${lines.join(', ')}`;
}

function CompanyResults({
  companies,
  applied,
  toSheetRow,
}: {
  companies: AccountLeadImportCompanyResult[];
  applied: boolean;
  toSheetRow: ToSheetRow;
}) {
  return (
    <>
      <div className="max-md:hidden">
        <TableWrap frozenHeader frozenMaxHeight="min(55vh, 520px)">
          <Table className="min-w-[720px]">
            <THead>
              <TR>
                <TH>Company</TH>
                <TH>Before</TH>
                <TH>After</TH>
                <TH>Status</TH>
              </TR>
            </THead>
            <TBody>
              {companies.map((company, i) => (
                <TR key={companyKey(company, i)}>
                  <TD className="align-top">
                    <span className="[overflow-wrap:anywhere]">{companyLabel(company, toSheetRow)}</span>
                  </TD>
                  <TD className="align-top">
                    <PeriodList periods={company.before} />
                  </TD>
                  <TD className="align-top">
                    <PeriodList periods={company.after} />
                  </TD>
                  <TD className="align-top">
                    <StatusCell company={company} applied={applied} toSheetRow={toSheetRow} />
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </TableWrap>
      </div>

      <ul className="m-0 grid list-none gap-2 p-0 md:hidden">
        {companies.map((company, i) => (
          <li
            key={companyKey(company, i)}
            className="grid gap-2 rounded-md border border-border bg-surface px-3 py-2.5 text-sm"
          >
            <div className="font-medium text-fg [overflow-wrap:anywhere]">{companyLabel(company, toSheetRow)}</div>
            <dl className="m-0 grid gap-2">
              <div>
                <dt className="text-xs text-fg-muted">Before</dt>
                <dd className="m-0">
                  <PeriodList periods={company.before} />
                </dd>
              </div>
              <div>
                <dt className="text-xs text-fg-muted">After</dt>
                <dd className="m-0">
                  <PeriodList periods={company.after} />
                </dd>
              </div>
              <div>
                <dt className="text-xs text-fg-muted">Status</dt>
                <dd className="m-0">
                  <StatusCell company={company} applied={applied} toSheetRow={toSheetRow} />
                </dd>
              </div>
            </dl>
          </li>
        ))}
      </ul>
    </>
  );
}
